import PublicBreadcrumbs from '../components/common/PublicBreadcrumbs.jsx';
import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import { usePublicShareData } from '../hooks/usePublicShareData.js';
import { offerSeoPolicy } from '../utils/offerSeoPolicy.js';
import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  ExternalLink,
  MapPin,
  Phone,
  QrCode,
  Save,
  Ticket,
  X,
} from 'lucide-react';
import { buildListingPath } from '../utils/listingUrls';
import { Button, Card, ContentRenderer, OfferBusinessCard, OfferCTA, OfferDetails, OfferHero, OfferTerms, PublicContainer, isContentEditorDocumentEmpty, ShareButton } from '@mts/ui';
import { buildOfferDisplayModel } from '@mts/shared/offerDisplay';
import {
  getOffer,
  getListingOffers,
  requestOffer,
  reportOffer,
  saveOffer,
  trackOfferEvent,
} from '../services/offersService';
import { trackVisitorEvent } from '../services/visitorTracking';
import PublicOfferCard from '../components/offers/OfferCard.jsx';
import { usePublicTenant } from '../hooks/usePublicTenant';
import { buildCanonicalUrl } from '../utils/canonicalUrls.js';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildOfferSchema, buildWebPageSchema } from '../utils/structuredData.js';
import NotFoundPage from './NotFoundPage.jsx';

function normalizeAddress(offer) {
  const listing = offer?.listing || {};
  if (listing.address && String(listing.address).trim()) return String(listing.address).trim();

  const city = String(listing.city || '').trim();
  const state = String(listing.state || '').trim();
  const zip = String(listing.zipCode || listing.zip || '').trim();
  return [city, state, zip].filter(Boolean).join(', ');
}

function buildMapsLink(offer) {
  const address = normalizeAddress(offer);
  const fallback = String(offer?.listing?.name || '').trim();
  const query = address || fallback;
  if (!query) return null;
  return `https://maps.google.com/?q=${encodeURIComponent(query)}`;
}

function OfferHeroSection({
  offer,
  display,
  saving,
  reportingRedemption,
  onRedeem,
  onSave,
  shareData,
  onToggleQr,
  previewPhotos = [],
  isPreview = false,
}) {
  const image = display.image;
  const hasPreviewPhotos = isPreview && previewPhotos.length > 0;
  const previewImage = hasPreviewPhotos
    ? (previewPhotos[0]?.src?.large2x || previewPhotos[0]?.src?.large || previewPhotos[0]?.src?.medium || previewPhotos[0]?.imageUrl || image)
    : image;
  const primaryAction = <Button type="button" variant="primary" className="hidden gap-2 lg:inline-flex" onClick={onRedeem} disabled={reportingRedemption || display.expired}><Ticket size={16} />{display.expired ? 'Offer Ended' : reportingRedemption ? 'Getting offer...' : display.action.label}</Button>;
  const secondaryActions = <><Button type="button" variant="outline" className="gap-2" onClick={onSave} disabled={saving || display.expired}><Save size={16} />{saving ? 'Recording...' : 'Record interest'}</Button>{shareData ? <ShareButton {...shareData} size="md" className="gap-2" /> : null}<Button type="button" variant="outline" className="gap-2" onClick={onToggleQr}><QrCode size={16} />QR</Button></>;
  return <OfferHero display={display} imageUrl={previewImage} imageBadge={hasPreviewPhotos ? 'AI Recommended' : ''} primaryAction={primaryAction} secondaryActions={secondaryActions} />;
}

function OfferDescriptionCard({ offer, display }) {
  const hasRichContent = offer?.contentJson?.doc && !isContentEditorDocumentEmpty(offer.contentJson);
  if (!hasRichContent && !display.about) return null;

  return (
    <Card className="space-y-4 border border-base-300 bg-base-100 p-5 shadow-sm">
      <OfferDetails display={display}>{hasRichContent ? <ContentRenderer preset="article" value={offer.contentJson} /> : null}</OfferDetails>
    </Card>
  );
}

function OfferTermsCard({ display }) {
  if (!display.terms.length && !display.endDate) return null;
  return (
    <Card id="finePrintClauses" className="space-y-3 border border-base-300 bg-base-100 p-5 shadow-sm">
      <OfferTerms display={display} />
    </Card>
  );
}

function OfferRedemptionCard({ display }) {
  return (
    <Card id="offer-redemption-card" className="space-y-2 border border-base-300 bg-base-100 p-5 shadow-sm">
      <OfferCTA display={display} />
    </Card>
  );
}

function BusinessCard({ offer, display, onInteraction }) {
  const listing = offer?.listing || {};
  const listingSlug = String(listing.slug || '').trim();
  const listingPath = buildListingPath(listing);
  const address = normalizeAddress(offer);

  return (
    <Card className="space-y-3 border border-base-300 bg-base-100 p-5 shadow-sm">
      <OfferBusinessCard display={{ ...display, address }} viewBusinessAction={listingSlug && listingPath ? <Link to={listingPath} className="btn btn-outline btn-sm">View Business</Link> : null} contactActions={<>{display.phone ? <a href={`tel:${display.phone}`} onClick={() => onInteraction?.('PHONE_CLICK')} className="btn btn-outline btn-sm gap-2"><Phone size={14} />Call</a> : null}{display.website ? <a href={display.website} onClick={() => onInteraction?.('WEBSITE_CLICK')} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm gap-2">Website<ExternalLink size={12} /></a> : null}{buildMapsLink(offer) ? <a href={buildMapsLink(offer)} onClick={() => onInteraction?.('DIRECTIONS_CLICK')} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm gap-2">Directions<MapPin size={12} /></a> : null}</>} />
    </Card>
  );
}

function RelatedOffersSection({ offers, businessName }) {
  const visibleOffers = offers.filter((item) => item.slug).slice(0, 3);
  if (!visibleOffers.length) return null;
  return (
    <Card className="space-y-3 border border-base-300 bg-base-100 p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-primary">More from {businessName}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {visibleOffers.map((item) => <PublicOfferCard key={item.id} offer={item} variant="compact" />)}
        </div>
    </Card>
  );
}

function StickyMobileCTA({ label, onRedeem, reportingRedemption }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-base-300 bg-base-100/95 px-4 py-3 backdrop-blur lg:hidden">
      <button type="button" className="btn btn-primary w-full gap-2" onClick={onRedeem} disabled={reportingRedemption}>
        <Ticket size={16} />
        {reportingRedemption ? 'Getting offer...' : label}
      </button>
    </div>
  );
}

function RedeemOfferModal({ offer, tenantName, open, error, submitting, unlocked, onSubmit, onClose, onShowOffer }) {
  if (!open) return null;

  const successActionLabel = offer?.listing?.name
    ? `Show this offer at ${offer.listing.name}.`
    : 'Show this offer at the business.';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 px-4 py-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="redeem-offer-title">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border border-base-300 bg-base-100 p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="redeem-offer-title" className="text-xl font-semibold text-base-content">
              {unlocked ? 'Offer Ready' : 'Use This Offer'}
            </h2>
            <p className="mt-1 text-sm text-base-content/70">
              {unlocked ? successActionLabel : 'Confirm when you are ready to show this offer to the business.'}
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Close redeem offer">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div className="rounded-md border border-primary/25 bg-primary/10 p-4">
            <p className="text-sm font-medium text-base-content">{offer?.title || 'This offer'}</p>
            <p className="mt-2 text-sm text-base-content/75">
              {offer?.terms || 'Show this offer to the business when you are ready to redeem it.'}
            </p>
            {unlocked && offer?.promoCode ? (
              <p className="mt-3 rounded bg-base-100 px-3 py-2 font-mono text-base font-semibold">{offer.promoCode}</p>
            ) : null}
          </div>
          {error ? <InlineFeedback variant="error" className="rounded-md border border-error/30 bg-error/10 p-3 text-sm text-error">{error}</InlineFeedback> : null}
          {unlocked ? (
            <Button type="button" variant="primary" className="w-full" onClick={onShowOffer}>
              {successActionLabel}
            </Button>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              {offer?.captureMode !== 'NONE' ? <><label className="grid gap-1 text-sm font-medium">Delivery method<select name="channel" defaultValue={offer?.captureMode === 'SMS' ? 'SMS' : 'EMAIL'} className="select select-bordered w-full">{offer?.captureMode !== 'SMS' ? <option value="EMAIL">Email</option> : null}{offer?.captureMode !== 'EMAIL' ? <option value="SMS">Text message</option> : null}</select></label>{offer?.captureMode !== 'SMS' ? <label className="grid gap-1 text-sm font-medium">Email<input name="email" type="email" className="input input-bordered w-full" /></label> : null}{offer?.captureMode !== 'EMAIL' ? <label className="grid gap-1 text-sm font-medium">Mobile phone<input name="phone" type="tel" className="input input-bordered w-full" /></label> : null}<div className="space-y-2 rounded-md bg-base-200 p-3 text-sm"><p className="font-medium">Optional updates</p><label className="flex gap-2"><input name="tenantMarketing" type="checkbox" className="checkbox checkbox-sm" />Send me local deals and updates from {tenantName}.</label><label className="flex gap-2"><input name="listingMarketing" type="checkbox" className="checkbox checkbox-sm" />Allow this business to send future promotions through {tenantName}.</label></div></> : <input type="hidden" name="channel" value="NONE" />}
              <Button type="submit" variant="primary" className="w-full" loading={submitting} disabled={submitting}>
                Get and Show Offer
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewBanner({ role, offerSlug }) {
  const label = role === 'owner'
    ? 'Owner Preview — this is how your offer page looks to the public'
    : 'Preview mode';

  return (
    <div className="sticky top-0 z-50 flex items-center justify-center gap-3 bg-amber-400 px-4 py-2 text-xs font-semibold text-amber-900 shadow-sm">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
      </svg>
      {label}
      {role === 'owner' && offerSlug ? <a href={`/manage/offers/${encodeURIComponent(offerSlug)}`} className="rounded bg-amber-950 px-2 py-1 text-amber-50">Return to Editing</a> : null}
    </div>
  );
}

export default function OfferDetailPage() {
  const toast = useFeedback();
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const { slug: offerSlug } = useParams();
  const offerIdentifier = offerSlug;
  const [searchParams] = useSearchParams();
  const previewMode = searchParams.get('preview');
  const isEmbeddedPreview = previewMode === 'portal';
  const isPreview = isEmbeddedPreview || previewMode === 'owner';
  const placementId = searchParams.get('placementId') || null;
  const previewRole = searchParams.get('role') || null;
  const initial = useInitialPublicData("offer");
  const [offer, setOffer] = useState(initial || null);
  const [relatedOffers, setRelatedOffers] = useState([]);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reportingRedemption, setReportingRedemption] = useState(false);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [offerUnlocked, setOfferUnlocked] = useState(false);
  const [redemptionError, setRedemptionError] = useState('');

  const [showQr, setShowQr] = useState(false);
  const [previewPhotos, setPreviewPhotos] = useState([]);
  const previewDriverRef = useRef(null);

  useEffect(() => {
    if (!isEmbeddedPreview) return;

    function getPreviewDriver() {
      if (!previewDriverRef.current) {
        previewDriverRef.current = driver({
          animate: true,
          allowClose: false,
          overlayColor: 'rgba(0, 0, 0, 0.68)',
          stagePadding: 8,
          stageRadius: 10,
          showButtons: [],
          disableActiveInteraction: false,
        });
      }
      return previewDriverRef.current;
    }

    function highlightTarget(fieldId, scrollIntoView = true) {
      const normalizedFieldId = String(fieldId || '').trim();
      const instance = getPreviewDriver();

      if (!normalizedFieldId) {
        instance.destroy();
        return;
      }

      const target = document.getElementById(normalizedFieldId);
      if (!target) {
        instance.destroy();
        return;
      }

      if (scrollIntoView) {
        target.scrollIntoView({ block: 'center', inline: 'nearest' });
      }

      requestAnimationFrame(() => {
        instance.highlight({
          element: target,
        });
      });
    }

    function handleMessage(event) {
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'mts:preview-init') {
        const offerData = event.data.offer;
        if (offerData && typeof offerData === 'object') {
          setOffer({ ...offerData });
          setLoading(false);
          setError(null);
        }
        return;
      }

      if (event.data.type === 'mts:preview-photos') {
        const photos = Array.isArray(event.data.photos) ? event.data.photos : [];
        setPreviewPhotos(photos);
        return;
      }

      if (event.data.type === 'mts:preview-highlight-target') {
        const fieldId = String(event.data.fieldId || '').trim();
        const shouldScroll = event.data.scrollIntoView !== false;
        highlightTarget(fieldId, shouldScroll);
      }

    }

    function handlePreviewLinkClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[href]');
      if (!anchor) return;
      event.preventDefault();
      event.stopPropagation();
    }

    function signalPreviewLoading() {
      window.parent.postMessage({ type: 'mts:preview-loading' }, '*');
    }

    window.addEventListener('message', handleMessage);
    document.addEventListener('click', handlePreviewLinkClick, true);
    document.addEventListener('auxclick', handlePreviewLinkClick, true);
    window.addEventListener('beforeunload', signalPreviewLoading);
    // Signal to the portal frame that we are ready to receive data.
    window.parent.postMessage({ type: 'mts:preview-ready' }, '*');
    return () => {
      if (previewDriverRef.current) {
        previewDriverRef.current.destroy();
        previewDriverRef.current = null;
      }
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('click', handlePreviewLinkClick, true);
      document.removeEventListener('auxclick', handlePreviewLinkClick, true);
      window.removeEventListener('beforeunload', signalPreviewLoading);
    };
  }, [isEmbeddedPreview]);

  const pageUrl = useMemo(() => {
    return buildCanonicalUrl(tenant, `/offers/${encodeURIComponent(offer?.slug || offerIdentifier)}`);
  }, [offer?.slug, offerIdentifier, tenant]);
  const display = useMemo(
    () => buildOfferDisplayModel(offer || {}, { tenant: offer?.tenant || { name: offer?.tenantName } }),
    [offer],
  );

  const shareMetadata = useMemo(() => {
    if (!offer) return null;
    const policy = offerSeoPolicy(offer, { preview: isPreview });
    const path = `/offers/${encodeURIComponent(offer.slug || offerIdentifier)}`;
    const description = !policy.indexable && offer.computedStatus === 'EXPIRED'
      ? 'Historical offer. This promotion has expired and is not redeemable.'
      : offer.seoDescription || offer.shortDescription || offer.description || (policy.indexable ? '' : 'This offer is not currently available.');
    const title = policy.indexable ? offer.seoTitle || `${offer.title}${offer.listing?.name ? ` at ${offer.listing.name}` : ''} | ${spotlightName}` : `${offer.title || 'Offer'} | ${spotlightName}`;
    const metadata = buildPublicPageMetadata({ tenant, pathname: path, canonicalPath: path, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title, description, imageUrl: display.image, imageAlt: offer.title, robots: policy.indexable ? 'index,follow' : 'noindex,follow', jsonLd: [] });
    if (!isPreview && pageUrl && tenant?.publicIndexingAllowed !== false) metadata.canonicalUrl = pageUrl;
    if (policy.indexable && metadata.canonicalUrl) {
      const seller = offer.listing ? { '@type': 'LocalBusiness', name: offer.listing.name, url: offer.listing.publicUrl ? new URL(offer.listing.publicUrl, metadata.canonicalUrl).toString() : undefined, telephone: offer.listing.phone, image: offer.listing.logoUrl } : undefined;
      metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: title, description }), ...(policy.activeDeal ? [buildOfferSchema({ url: metadata.canonicalUrl, name: offer.title, description, image: display.image, validFrom: offer.validFrom, validThrough: offer.validThrough, seller })] : []), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Offers', url: `${tenant.canonicalOrigin}/offers` }, { name: offer.title, url: metadata.canonicalUrl }] })];
    }
    return metadata;
  }, [display.image, offer, isPreview, offerIdentifier, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl, pageUrl]);
  const shareData = usePublicShareData({ contentType: 'offer', title: offer?.title, metadata: shareMetadata, hidden: isPreview || !offer || (offer.visibility != null && offer.visibility !== 'PUBLIC'), status: offer?.status, publicationStatus: offer?.publicationStatus });

  usePublicMetadata(() => {
    if (!loading && shareMetadata) return (shareMetadata);
  }, [loading, shareMetadata]);

  usePublicMetadata(() => {
    if (!loading && (error || !offer)) return (buildPublicPageMetadata({ tenant, pathname: `/offers/${encodeURIComponent(offerIdentifier)}`, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: `Offer not found | ${spotlightName}`, description: 'The requested offer is not available.', robots: 'noindex,follow', jsonLd: [] }));
  }, [error, loading, offer, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  useEffect(() => {
    if (isEmbeddedPreview) return;

    let cancelled = false;

    async function load() {
      try {
        if (!initial) setLoading(true);
        setError(null);


        const loadedOffer = await getOffer(offerIdentifier);
        if (cancelled) return;

        setOffer(loadedOffer);
        trackOfferEvent(offerIdentifier, 'VIEW', { placementId, metadataJson: { surface: 'offer_detail' } }).catch(() => null);

        const listingId = loadedOffer?.listing?.id;
        if (listingId) {
          const items = await getListingOffers(listingId).catch(() => []);
          if (cancelled) return;
          setRelatedOffers(items.filter((item) => String(item.id) !== String(loadedOffer.id)));
        } else {
          setRelatedOffers([]);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [offerIdentifier, isEmbeddedPreview, placementId, initial]);

  const canEditOffer = Boolean(offer?.canEdit && !isPreview);

  const handleRedeem = () => {
    if (!offerIdentifier || display?.expired) return;
    const externalDestination = display.action.href;
    if (offer?.captureMode === 'NONE' && externalDestination) {
      trackOfferEvent(offerIdentifier, 'CTA_CLICK', { placementId, metadataJson: { action: 'external_cta' } }).catch(() => null);
      window.location.assign(externalDestination);
      return;
    }
    setRedemptionError('');
    setRedeemModalOpen(true);
    trackOfferEvent(offerIdentifier, 'CTA_CLICK', { placementId, metadataJson: { action: 'redeem' } }).catch(() => null);
    trackVisitorEvent({
      eventType: 'offer.redeem.started',
      listingId: offer?.listing?.id || offer?.listingId || null,
      metadata: { offerId: offer?.id || null },
    });
  };

  const handleReportOffer = async () => {
    const reason = window.prompt('Report reason: NOT_HONORED, EXPIRED, INCORRECT_INFORMATION, MISLEADING_PROMOTION, or OTHER');
    if (!reason) return;
    try { await reportOffer(offerIdentifier, { reason: reason.trim().toUpperCase() }); toast.success('Thanks. This Offer was sent to the local review team.', { id: 'action:OfferDetailPage:handleReportOffer' }); }
    catch (reportError) { toast.error(reportError?.message || 'Unable to submit the report.', { id: 'action:OfferDetailPage:handleReportOffer' }); }
  };

  const handleUnlockOffer = async (event) => {
    event.preventDefault();
    if (!offerIdentifier) return;

    try {
      setReportingRedemption(true);
      setRedemptionError('');


      const form = new FormData(event.currentTarget);
      const channel = String(form.get('channel') || 'NONE');
      const result = await requestOffer(offerIdentifier, { channel, email: String(form.get('email') || ''), phone: String(form.get('phone') || ''), sourceContext: isPreview ? 'offer_preview' : 'offer_page', placementId, referrer: document.referrer || null, utmSource: searchParams.get('utm_source'), utmMedium: searchParams.get('utm_medium'), utmCampaign: searchParams.get('utm_campaign'), utmContent: searchParams.get('utm_content'), consents: { tenantEmail: channel === 'EMAIL' && form.get('tenantMarketing') === 'on', tenantSms: channel === 'SMS' && form.get('tenantMarketing') === 'on', listingEmail: channel === 'EMAIL' && form.get('listingMarketing') === 'on', listingSms: channel === 'SMS' && form.get('listingMarketing') === 'on' } });

      if (result?.offer?.id) {
        setOffer(result.offer);
      }
      setOfferUnlocked(Boolean(result?.unlocked));
      toast.success('Offer ready. Show it to the business when you are ready.', { id: 'action:OfferDetailPage:handleUnlockOffer' });
      if (result?.unlocked && (result?.offer?.promoCode || offer?.promoCode)) {
        trackOfferEvent(offerIdentifier, 'PROMO_CODE_REVEAL', { placementId, metadataJson: { surface: 'redemption_modal' } }).catch(() => null);
      }

    } catch (err) {
      toast.error(err?.message || 'Could not unlock this offer right now. Please try again.', { id: 'action:OfferDetailPage:handleUnlockOffer' });
    } finally {
      setReportingRedemption(false);
    }
  };

  const handleShowUnlockedOffer = () => {
    setRedeemModalOpen(false);
    window.setTimeout(() => {
      document.getElementById('offer-redemption-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleSave = async () => {
    if (!offerIdentifier) return;

    try {
      setSaving(true);

      await saveOffer(offerIdentifier, { placementId });
      toast.success('Interest recorded.', { id: 'action:OfferDetailPage:handleSave' });
    } catch (err) {
      toast.error(err?.message || 'Please log in to record your interest.', { id: 'action:OfferDetailPage:handleSave' });
    } finally {
      setSaving(false);
    }
  };

  const handleContactInteraction = (eventType) => {
    trackOfferEvent(offerIdentifier, eventType, { placementId, metadataJson: { surface: 'offer_detail' } }).catch(() => null);
  };


  if (loading) {
    return (
      <section className="py-6 md:py-8">
        <PublicContainer>
          <Card className="border border-base-300 bg-base-100 p-6 text-sm text-base-content/70">Loading offer...</Card>
        </PublicContainer>
      </section>
    );
  }

  if (error?.status === 404 || (!error && !offer)) return <NotFoundPage resource="offer" />;

  if (error) {
    return (
      <section className="py-6 md:py-8">
        <PublicContainer>
          <Card className="space-y-3 border border-base-300 bg-base-100 p-6">
            <h1 className="text-lg font-semibold text-base-content">Offers are temporarily unavailable</h1>
            <InlineFeedback variant="error" className="text-sm text-base-content/75">{error.message}</InlineFeedback>
            <Link to="/offers" className="btn btn-primary btn-sm w-fit">Back to Deals</Link>
          </Card>
        </PublicContainer>
      </section>
    );
  }

  const qrImageSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(pageUrl || '')}`;
  return (
    <section className="relative pb-24 pt-5 md:pb-8 md:pt-8">
      {isPreview ? <PreviewBanner role={previewRole} offerSlug={offer.slug} /> : null}
      <PublicContainer>
        <div className="space-y-5 md:space-y-6">
          <PublicBreadcrumbs items={[{ name: 'Home', href: '/' }, { name: 'Offers', href: '/offers' }, { name: offer.title }]} />
          {canEditOffer ? <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3"><p className="text-sm font-semibold text-base-content">You’re viewing your published Offer</p><div className="flex gap-2"><a href={`/manage/offers/${encodeURIComponent(offer.slug)}`} className="btn btn-primary btn-sm">Edit Offer</a><a href={`/manage/offers/${encodeURIComponent(offer.slug)}#performance`} className="btn btn-outline btn-sm">View Analytics</a></div></div> : null}
          <OfferHeroSection
            offer={offer}
            display={display}
            saving={saving}
            reportingRedemption={reportingRedemption}
            onRedeem={handleRedeem}
            onSave={handleSave}
            shareData={shareData}
            onToggleQr={() => setShowQr((value) => !value)}
            previewPhotos={previewPhotos}
            isPreview={isEmbeddedPreview}
          />

          {showQr ? (
            <Card className="w-fit border border-base-300 bg-base-100 p-4 shadow-sm">
              <p className="mb-3 text-sm font-medium text-base-content">Scan to open this offer</p>
              <img src={qrImageSrc} alt="Offer QR code" className="h-44 w-44 rounded-lg border border-base-300" />
            </Card>
          ) : null}



          {display.expired ? <Card className="border border-amber-200 bg-amber-50 p-5"><h2 className="text-lg font-semibold text-amber-950">This Offer has ended</h2><p className="mt-1 text-sm text-amber-900">See current Offers from {display.businessName} or browse current {display.tenantName} Offers.</p><div className="mt-3 flex flex-wrap gap-2"><Link to={offer.listing?.publicPath || '/offers'} className="btn btn-outline btn-sm">View {display.businessName}</Link><Link to="/offers" className="btn btn-primary btn-sm">Browse Current Offers</Link></div></Card> : null}

          <div className="grid gap-4 lg:grid-cols-[1.35fr,0.85fr] lg:gap-5">
            <div className="space-y-4">
              <OfferDescriptionCard
                offer={offer}
                display={display}
              />
              {!display.expired ? <OfferRedemptionCard display={display} /> : null}
              <OfferTermsCard display={display} />
              <RelatedOffersSection offers={relatedOffers} businessName={display.businessName} />
              <button type="button" onClick={handleReportOffer} className="text-left text-sm text-base-content/60 underline hover:text-primary">Report this Offer</button>
            </div>

            <div className="space-y-4">
              <BusinessCard offer={offer} display={display} onInteraction={handleContactInteraction} />
            </div>
          </div>
        </div>
      </PublicContainer>

      <RedeemOfferModal
        offer={offer}
        tenantName={display.tenantName}
        open={redeemModalOpen}
        error={redemptionError}
        submitting={reportingRedemption}
        unlocked={offerUnlocked}
        onSubmit={handleUnlockOffer}
        onClose={() => setRedeemModalOpen(false)}
        onShowOffer={handleShowUnlockedOffer}
      />

      {!display.expired ? <StickyMobileCTA label={display.action.label} onRedeem={handleRedeem} reportingRedemption={reportingRedemption} /> : null}
    </section>
  );
}
